import React, { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, useAuth } from "../App";
import { toast } from "sonner";
import { ArrowLeft, Share2, TrendingUp, TrendingDown } from "lucide-react";

/**
 * Review Instance — the rapid-rate per-person review.
 * Objective half is computed from the tool (live work + uploaded work log);
 * your judgment is tap-chips (1-5) that auto-save the moment all three
 * pillars are set. Leaders get portfolio roll-ups + an upward voice instead
 * of per-item tables.
 */

const PILLARS = ["hook", "craft", "fit"];
const PILLAR_LABEL = { hook: "Hook", craft: "Craft", fit: "Fit" };
// Section A — the twice-yearly 360 rates the PERSON in the round on these
// idea + behavioural dimensions (separate from the per-item Hook/Craft/Fit).
const SECA_DIMS = [
  ["originality", "Originality", "fresh thinking, not derivative"],
  ["collaboration", "Collaboration", "how they are to work with"],
  ["reliability", "Reliability", "owns deadlines and commitments"],
  ["growth", "Growth", "trajectory — improving over time"],
];
const BAND_COLORS = { HERO: "#16a34a", STRONG: "#2563eb", AVERAGE: "#d97706", RETHINK: "#dc2626" };
const VOICE_LABEL = { self: "Self", manager: "Manager", peer: "Peer", downstream: "Downstream", upward: "Upward (report)" };

function Band({ band, big }) {
  if (!band || !band.label) return <span style={{ color: "var(--t3)" }}>—</span>;
  return (
    <span style={{ fontSize: big ? 18 : 12, fontWeight: 800, color: BAND_COLORS[band.label] || "#666" }}>
      {band.emoji} {band.label}
    </span>
  );
}

/** 1-5 tap chips. Tap to set; highlighted when chosen. */
function ChipScore({ value, onPick, saved }) {
  return (
    <span style={{ display: "inline-flex", gap: 4 }}>
      {[1, 2, 3, 4, 5].map((n) => {
        const active = value === n;
        return (
          <button key={n} onClick={() => onPick(n)} style={{
            width: 30, height: 30, borderRadius: 8, cursor: "pointer", fontSize: 13,
            fontWeight: active ? 800 : 500,
            border: `1.5px solid ${active ? (saved ? "#16a34a" : "#1a56db") : "var(--bdr)"}`,
            background: active ? (saved ? "rgba(22,163,74,.18)" : "rgba(26,86,219,.2)") : "var(--s2)",
            color: active ? (saved ? "#4ade80" : "#7ca6f7") : "var(--t2)",
          }}>{n}</button>
        );
      })}
    </span>
  );
}

export default function ReviewInstancePage() {
  const { instanceId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [inst, setInst] = useState(null);
  const [loading, setLoading] = useState(true);
  const [secA, setSecA] = useState({ originality: null, collaboration: null, reliability: null, growth: null });
  const [secASaved, setSecASaved] = useState(false);
  const [notes, setNotes] = useState("");
  const [itemScores, setItemScores] = useState({});   // item_id -> {hook,craft,fit, saved}
  const [busy, setBusy] = useState(false);
  const notesTimer = useRef(null);

  const myUid = user?.user_id || user?.id;

  const load = useCallback(async (quiet = false) => {
    if (!quiet) setLoading(true);
    try {
      const res = await api.get(`/reviews/instances/${instanceId}`);
      setInst(res.data);
      const mine = (res.data.section_a_ratings || []).find((r) => r.reviewer_id === myUid);
      if (mine) {
        setSecA({ hook: mine.scores?.hook ?? null, craft: mine.scores?.craft ?? null, fit: mine.scores?.fit ?? null });
        setSecASaved(true);
        setNotes(mine.prompts?.notes || "");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't load review");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, [instanceId, myUid]);

  useEffect(() => { load(); }, [load]);

  const myReviewer = inst?.reviewers?.find((r) => r.user_id === myUid);
  const roleKey = (user?.role || "").toLowerCase();
  const isAdminish = ["admin", "srmanager", "manager"].includes(roleKey);
  const isManager = inst && (inst.manager_id === myUid);

  // ── Section A: auto-save once all three pillars are tapped
  const tapSecA = async (pillar, n) => {
    const next = { ...secA, [pillar]: n };
    setSecA(next);
    setSecASaved(false);
    if (SECA_DIMS.every(([k]) => next[k])) {
      try {
        await api.post(`/reviews/instances/${instanceId}/section-a`, {
          scores: next, prompts: { notes },
        });
        setSecASaved(true);
        load(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Save failed");
      }
    }
  };

  const saveNotes = (val) => {
    setNotes(val);
    if (notesTimer.current) clearTimeout(notesTimer.current);
    if (SECA_DIMS.every(([k]) => secA[k])) {
      notesTimer.current = setTimeout(async () => {
        try {
          await api.post(`/reviews/instances/${instanceId}/section-a`, { scores: secA, prompts: { notes: val } });
        } catch (e) { /* silent — chips already saved */ }
      }, 900);
    }
  };

  // ── Per-item: auto-save when all 3 pillars set
  const tapItem = async (craft, item, pillar, n) => {
    const cur = itemScores[item.item_id] || {};
    const next = { ...cur, [pillar]: n, saved: false };
    setItemScores((p) => ({ ...p, [item.item_id]: next }));
    if (next.hook && next.craft && next.fit) {
      try {
        await api.post(`/reviews/items/${item.item_type}/${item.item_id}/rate`, {
          craft, scores: { hook: next.hook, craft: next.craft, fit: next.fit },
        });
        setItemScores((p) => ({ ...p, [item.item_id]: { ...next, saved: true } }));
        load(true);
      } catch (e) {
        toast.error(e?.response?.data?.detail || "Rating failed");
      }
    }
  };

  const share = async () => {
    if (!window.confirm("Share this review with the person? They will be notified.")) return;
    setBusy(true);
    try {
      await api.post(`/reviews/instances/${instanceId}/share`);
      toast.success("Review shared");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Share failed");
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!inst) return <div style={{ padding: 24 }}>Not found.</div>;

  const computed = inst.computed || {};
  const perCraft = computed.per_craft || {};
  const sectionB = computed.section_b_by_craft || {};
  const prev = inst.previous;

  return (
    <div className="dm-page-container" style={{ padding: 24, maxWidth: 1000, margin: "0 auto" }}>
      <button onClick={() => navigate(-1)} style={{ display: "flex", alignItems: "center", gap: 6, color: "var(--t3)", fontSize: 13, marginBottom: 16, background: "none", border: "none", cursor: "pointer" }}>
        <ArrowLeft size={16} /> BACK
      </button>

      {/* Header: person + composite + trajectory */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1 style={{ fontSize: 26, fontWeight: 800 }}>{inst.subject_name || inst.subject_id}</h1>
          <p style={{ color: "var(--t3)", fontSize: 13, marginTop: 4 }}>
            {(inst.crafts || []).map((c) => (sectionB[c]?.label || c)).join(" · ") || "—"} · Manager: {inst.manager_name || "—"} · <span style={{ textTransform: "uppercase" }}>{inst.status}</span>
          </p>
        </div>
        <div style={{ textAlign: "right" }}>
          <div style={{ fontSize: 32, fontWeight: 800 }}>{computed.composite != null ? computed.composite.toFixed(2) : "—"}</div>
          <Band band={computed.band} big />
          {prev && prev.delta != null && (
            <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 4, fontSize: 12.5, marginTop: 4, color: prev.delta >= 0 ? "#4ade80" : "#f87171" }}>
              {prev.delta >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
              {prev.composite.toFixed(2)} → {computed.composite?.toFixed(2)} ({prev.delta >= 0 ? "+" : ""}{prev.delta.toFixed(2)} vs mid-year)
            </div>
          )}
          {computed.confidence === "subjective_only" && (
            <div style={{ fontSize: 11, color: "var(--t3)", marginTop: 2 }}>subjective-only (no objective KPIs yet)</div>
          )}
        </div>
      </div>

      {/* Your universal judgment — one chip strip, autosaves */}
      {myReviewer && (
        <div style={{ background: "var(--s1)", border: `1px solid ${secASaved ? "rgba(22,163,74,.4)" : "var(--bdr)"}`, borderRadius: 12, padding: "16px 18px", marginBottom: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".05em" }}>
              Your judgment ({VOICE_LABEL[myReviewer.voice] || myReviewer.voice})
            </span>
            {secASaved && <span style={{ fontSize: 12, color: "#4ade80", fontWeight: 600 }}>✓ saved</span>}
          </div>
          <p style={{ fontSize: 12, color: "var(--t3)", margin: "0 0 14px", lineHeight: 1.5 }}>
            The twice-yearly 360 — rate the person in the round, beyond any single piece. The work itself is scored Hook/Craft/Fit as it ships.
          </p>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {SECA_DIMS.map(([key, label, hint]) => (
              <div key={key} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
                <span style={{ fontSize: 13, color: "var(--t2)" }}>
                  {label} <span style={{ color: "var(--t4, #6b6b78)" }}>· {hint}</span>
                </span>
                <ChipScore value={secA[key]} saved={secASaved} onPick={(n) => tapSecA(key, n)} />
              </div>
            ))}
          </div>
          <input value={notes} onChange={(e) => saveNotes(e.target.value)}
            placeholder="Optional notes — what stood out, growth edge, promotion readiness…"
            style={{ width: "100%", marginTop: 14, padding: "9px 12px", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 8, color: "var(--t1)", fontSize: 13 }} />
        </div>
      )}

      {/* Per-craft scorecards */}
      {(inst.crafts || []).map((craft) => {
        const b = sectionB[craft] || {};
        const pc = perCraft[craft] || {};
        const isLeader = b.archetype === "leader";
        return (
          <div key={craft} style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 18, marginBottom: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
              <h2 style={{ fontSize: 16, fontWeight: 700 }}>{b.label || craft}</h2>
              <div style={{ fontSize: 13, color: "var(--t3)" }}>
                {isLeader ? "portfolio roll-up" : `${b.item_count || 0} items`} · final {pc.final != null ? pc.final.toFixed(2) : "—"} · <Band band={pc.band} />
              </div>
            </div>

            {/* Computed from the tool — objective KPI strip */}
            {(b.person_kpis || []).length > 0 && (
              <div style={{ display: "flex", gap: 18, flexWrap: "wrap", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 10, padding: "10px 14px", marginBottom: 12 }}>
                <span style={{ fontSize: 10.5, color: "var(--t3)", textTransform: "uppercase", letterSpacing: ".06em", alignSelf: "center" }}>Computed from the tool</span>
                {b.person_kpis.map((k, i) => (
                  <span key={i} style={{ fontSize: 13 }}>
                    <span style={{ color: "var(--t3)" }}>{k.label}: </span>
                    <b>{k.value}</b>
                  </span>
                ))}
                {pc.objective != null && <span style={{ fontSize: 13 }}><span style={{ color: "var(--t3)" }}>Objective score: </span><b>{pc.objective.toFixed(2)}</b></span>}
                {isLeader && pc.upward != null && <span style={{ fontSize: 13 }}><span style={{ color: "var(--t3)" }}>Upward voice: </span><b>{pc.upward.toFixed(2)}</b></span>}
              </div>
            )}

            {/* Items — tap to rate */}
            {!isLeader && (b.items || []).length > 0 && (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse", minWidth: 640 }}>
                  <thead>
                    <tr style={{ color: "var(--t3)", textAlign: "left" }}>
                      <th style={{ padding: "6px" }}>Item</th>
                      <th style={{ padding: "6px" }}>Month</th>
                      <th style={{ padding: "6px" }}>Obj</th>
                      <th style={{ padding: "6px" }}>Avg so far</th>
                      <th style={{ padding: "6px" }}>Rate: Hook · Craft · Fit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(b.items || []).map((it) => {
                      const s = itemScores[it.item_id] || {};
                      return (
                        <tr key={it.item_id} style={{ borderTop: "1px solid var(--bdr)" }}>
                          <td style={{ padding: "8px 6px", maxWidth: 230, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                            {it.link ? <a href={it.link} target="_blank" rel="noreferrer" style={{ color: "var(--t1)" }}>{it.title} ↗</a> : it.title}
                            <span style={{ color: "var(--t3)", fontSize: 11, marginLeft: 6 }}>{it.kind}</span>
                          </td>
                          <td style={{ padding: "8px 6px", color: "var(--t3)" }}>{it.month || (it.date || "").slice(0, 7)}</td>
                          <td style={{ padding: "8px 6px" }}>{it.objective_score != null ? it.objective_score.toFixed(1) : "—"}</td>
                          <td style={{ padding: "8px 6px", color: "var(--t3)" }}>
                            {PILLARS.map((p) => it.pillars?.[p] != null ? it.pillars[p].toFixed(1) : "–").join(" · ")}
                            {it.ratings_count > 0 && <span style={{ fontSize: 11 }}> ({it.ratings_count})</span>}
                          </td>
                          <td style={{ padding: "8px 6px" }}>
                            <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                              {PILLARS.map((p) => (
                                <ChipScore key={p} value={s[p]} saved={s.saved} onPick={(n) => tapItem(craft, it, p, n)} />
                              ))}
                              {s.saved && <span style={{ fontSize: 11, color: "#4ade80" }}>✓</span>}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
            {!isLeader && (b.items || []).length === 0 && (
              <p style={{ color: "var(--t3)", fontSize: 13 }}>
                {["account_manager", "group_account_director", "influencer_mm"].includes(craft) ? (
                  <>No work found for this period — log retainer months / campaigns in the <a href="/reviews/work-log" style={{ color: "#7ca6f7" }}>retainer &amp; campaign log</a>.</>
                ) : (
                  <>No work found for this period. This pulls automatically from shipped work — add or backfill it in <a href="/production/backfill" style={{ color: "#7ca6f7" }}>Production</a> and it appears here.</>
                )}
              </p>
            )}
          </div>
        );
      })}

      {/* 360 panel */}
      <div style={{ background: "var(--s1)", border: "1px solid var(--bdr)", borderRadius: 12, padding: 18, marginBottom: 16 }}>
        <h2 style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Voices (360°)</h2>
        <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
          {(inst.reviewers || []).map((r, i) => (
            <span key={i} style={{ fontSize: 12, padding: "6px 10px", background: "var(--s2)", border: "1px solid var(--bdr)", borderRadius: 999 }}>
              <b>{VOICE_LABEL[r.voice] || r.voice}</b>: {r.voice === "upward" ? "anonymous report" : (r.name || r.user_id)} · <span style={{ color: r.status === "submitted" ? "#16a34a" : "var(--t3)" }}>{r.status}</span>
            </span>
          ))}
        </div>
      </div>

      {(isManager || isAdminish) && inst.status !== "shared" && (
        <button disabled={busy} onClick={share} style={{ display: "flex", alignItems: "center", gap: 6, padding: "10px 18px", background: "#16a34a", color: "#fff", border: "none", borderRadius: 8, fontWeight: 600, cursor: "pointer" }}>
          <Share2 size={16} /> Share with {inst.subject_name || "person"}
        </button>
      )}
    </div>
  );
}
